/**
 * Page path utilities for nested pages
 * Handles building full paths from parentId/slug and finding pages by full path
 * 
 * Note: Supports both old format (url, parentUrl) and new format (slug, parentId)
 * Pages are now keyed by ID, not by slug
 */

/**
 * Get the slug from a page object (supports both old and new field names)
 */
function getSlug(page) {
    return page?.slug || page?.url || '';
}

/**
 * Get the parent ID from a page object (supports both old and new field names)
 */
export function getParentId(page) {
    return page?.parentId || page?.parentUrl || null;
}

/**
 * Find a page by slug in the pages object
 * @param {string} slug - The slug to search for
 * @param {Object} pages - All pages object (pages[id] = page)
 * @returns {Object|null} { id, page } or null if not found
 */
export function findPageBySlug(slug, pages) {
    if (!slug || !pages) return null;
    
    for (const [id, page] of Object.entries(pages)) {
        const pageSlug = getSlug(page);
        if (pageSlug === slug) {
            return { id, page };
        }
    }
    return null;
}

/**
 * Get the full path for a page (e.g., "parent/child")
 * Recursively builds the path from parentId
 * @param {Object} page - Page object with slug and parentId
 * @param {Object} pages - All pages object (pages[id] = page)
 * @returns {string} Full path like "parent/child" or just "slug" if no parent
 */
export function getPagePath(page, pages) {
    const slug = getSlug(page);
    if (!slug) return '';
    
    const parentId = getParentId(page);
    if (!parentId || !pages || !pages[parentId]) {
        return slug;
    }
    
    const parent = pages[parentId];
    const parentPath = getPagePath(parent, pages);
    return parentPath ? `${parentPath}/${slug}` : slug;
}

/**
 * Find a page by its full path (e.g., "parent/child")
 * Traverses the path segments to find the page
 * @param {string} fullPath - Full path like "parent/child"
 * @param {Object} pages - All pages object (pages[id] = page)
 * @returns {Object|null} Page object or null if not found
 */
export function findPageByPath(fullPath, pages) {
    if (!fullPath || !pages) return null;
    
    const segments = fullPath.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    
    // Start with the last segment (leaf page slug)
    const leafSlug = segments[segments.length - 1];
    const leafResult = findPageBySlug(leafSlug, pages);
    
    if (!leafResult) return null;
    
    let page = leafResult.page;
    
    // If it's a single segment, verify it has no parent and return
    if (segments.length === 1) {
        const parentId = getParentId(page);
        return parentId ? null : page;
    }
    
    // For nested paths, verify the parent chain matches the path
    // Traverse from the leaf up to the root, checking each parent
    let currentPage = page;
    
    // We need to verify: segments[0] is root, segments[1] is child of segments[0], etc.
    // Start from the second-to-last segment and work backwards
    for (let i = segments.length - 2; i >= 0; i--) {
        const expectedParentSlug = segments[i];
        const parentId = getParentId(currentPage);
        
        if (!parentId) {
            return null; // Expected a parent but none found
        }
        
        const parentPage = pages[parentId];
        if (!parentPage) return null;
        
        const parentSlug = getSlug(parentPage);
        if (parentSlug !== expectedParentSlug) {
            return null; // Path doesn't match
        }
        
        currentPage = parentPage;
    }
    
    // After traversing, currentPage should be the root page (segments[0])
    // Verify it has no parent
    const rootParentId = getParentId(currentPage);
    if (rootParentId) {
        return null;
    }
    
    return page;
}

/**
 * Find a page by its full path and return both the page and its ID
 * @param {string} fullPath - Full path like "parent/child"
 * @param {Object} pages - All pages object (pages[id] = page)
 * @returns {Object|null} { id, page } or null if not found
 */
export function findPageByPathWithId(fullPath, pages) {
    if (!fullPath || !pages) return null;
    
    const segments = fullPath.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    
    // Start with the last segment (leaf page slug)
    const leafSlug = segments[segments.length - 1];
    const leafResult = findPageBySlug(leafSlug, pages);
    
    if (!leafResult) return null;
    
    const { id, page } = leafResult;
    
    // If it's a single segment, verify it has no parent and return
    if (segments.length === 1) {
        const parentId = getParentId(page);
        return parentId ? null : { id, page };
    }
    
    // For nested paths, verify the parent chain matches the path
    let currentPage = page;
    
    for (let i = segments.length - 2; i >= 0; i--) {
        const expectedParentSlug = segments[i];
        const parentId = getParentId(currentPage);
        
        if (!parentId) {
            return null;
        }
        
        const parentPage = pages[parentId];
        if (!parentPage) return null;
        
        const parentSlug = getSlug(parentPage);
        if (parentSlug !== expectedParentSlug) {
            return null;
        }
        
        currentPage = parentPage;
    }
    
    const rootParentId = getParentId(currentPage);
    if (rootParentId) {
        return null;
    }
    
    return { id, page };
}

/**
 * Whether a follow-up address still leads somewhere. It may be a page path
 * ("series", "/work/a"), a page id, or "home"; a version can remove the page
 * it named, and then there is nothing to advance to.
 * @param {string|null} url
 * @param {Object} pages - pages[id] = page
 * @returns {boolean}
 */
export function followUpExists(url, pages) {
    if (!url || typeof url !== 'string') return false;
    if (url === 'home' || /^(?:[a-z]+:)?\/\//i.test(url)) return true;
    const path = url.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');
    if (!path || /\.(?:img|vid)$/.test(path) || /^resources(?:\/|$)/.test(path)) return true;
    if (pages?.[path]) return true;
    return Boolean(findPageByPath(path, pages) || (!path.includes('/') && findPageBySlug(path, pages)));
}

/**
 * Resolve a per-page theme colour, walking up parents so a section can tint
 * its subpages without each child setting its own override.
 * @param {Object|null} page - Starting page (or home)
 * @param {Object} pages - pages[id] = page
 * @param {'themeBackground'|'themeText'|'themeSideMenuBackground'|'themeSideMenuText'|'themeHeaderBackground'|'themeHeaderText'|'themeCaption'} key
 * @returns {string|null}
 */
export function resolveInheritedThemeColor(page, pages, key) {
    let current = page || null;
    const seen = new Set();
    while (current && typeof current === 'object') {
        const value = current[key];
        if (value) return value;
        const parentId = getParentId(current);
        if (!parentId || !pages?.[parentId] || seen.has(parentId)) break;
        seen.add(parentId);
        current = pages[parentId];
    }
    return null;
}
